import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Flex } from '$components/layouts';
import { useTranslation } from '$hooks';
import { formatPrice } from '$lib/helpers';
import { Splash } from '$components/elements/product-image/components/splash';
import { B2CDeliveryMethod } from '$constants';
import { IOrderLineModel } from '$models';
import { Text } from '$components/elements/text';
import { Image } from '$components/elements/image';
import { ErrorText } from '$components/elements/error-text';

type Props = { line: IOrderLineModel };

export const ReceiptLineItemApp: FC<Props> = ({ line }): JSX.Element => {
    const { translate } = useTranslation();
    const theme = useTheme();

    return (
        <ContentCell>
            <LineImageWrapper>
                {!!line.primaryImageUrl && (
                    <>
                        <Image height={80} src={line.primaryImageUrl} />
                        {!!line?.itemPriceTagUrl && (
                            <SplashContainer>
                                <Splash src={line.itemPriceTagUrl} />
                            </SplashContainer>
                        )}
                    </>
                )}
            </LineImageWrapper>
            <Flex column flex>
                <Text bottomMargin="2" fontWeight="Bold">{`${line.quantity || 0 > 1 ? `${line.quantity} x ` : ''}${
                    line?.product?.name
                }`}</Text>

                <Flex justifyContent="space-between">
                    <InfoContainer column>
                        {!line.description && line?.product?.b2CDeliveryMethod !== B2CDeliveryMethod.Download && (
                            <Text dimmed noSpacing size={theme.fontSizes.xxs}>
                                {line?.product?.authors?.toString() || line?.product?.itemGroupName}
                            </Text>
                        )}

                        {!!line.description && (
                            <Text dimmed size={theme.fontSizes.xxs}>
                                {line.description}
                            </Text>
                        )}
                        {line.itemPriceTagValue && (
                            <Text dimmed size={theme.fontSizes.xxs}>{`${translate(
                                'checkout.orderConfirmation.itemPriceTagValueLabel'
                            )} ${line.itemPriceTagValue}`}</Text>
                        )}
                        {(line?.voucherDiscount || 0) > 0 && (
                            <Text dimmed size={theme.fontSizes.xxs}>
                                {line.voucher}
                            </Text>
                        )}
                        {(line?.mixAndMatchCampaignDiscount || 0) > 0 && line.mixAndMatchCampaignName !== null && (
                            <Text dimmed size={theme.fontSizes.xxs}>{`${translate(
                                'checkout.orderConfirmation.mixMatchLabel'
                            )} ${line.mixAndMatchCampaignName}`}</Text>
                        )}
                    </InfoContainer>
                    <Flex gap="extrasmall">
                        <Text size={theme.fontSizes.xxs} fontWeight="Bold">
                            {formatPrice(line.lineTotalPrice)}
                        </Text>
                        {!!line.lineDiscount && (
                            <Text dimmed size={theme.fontSizes.xxs}>
                                {formatPrice(line.lineDiscount)}
                            </Text>
                        )}
                    </Flex>
                </Flex>

                {line.hasErrors && <ErrorText>{line.errorMessage}</ErrorText>}
            </Flex>
        </ContentCell>
    );
};

const LineImageWrapper = styled.div(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    width: '60px',
    height: '80px',
    marginRight: theme.space[5],
    padding: theme.space[2],
    backgroundColor: theme.colors.imgBg,
    borderRadius: theme.general.borderRadius,
}));

const SplashContainer = styled.div({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '45%',
    height: 'auto',
});

const ContentCell = styled(Flex)(({ theme }) => ({
    alignItems: 'center',
    paddingTop: theme.space[3],
    paddingBottom: theme.space[3],
}));

const InfoContainer = styled(Flex)(({ theme }) => ({ paddingRight: theme.space[2] }));
